import React from 'react';
import {
  FormControl,
  FormLabel,
  FormControlLabel,
  InputAdornment,
  TextField,
  Radio,
  RadioGroup,
  Grid,
  Box,
  Select,
  MenuItem
} from '@mui/material';
import useStyles from '../useStyles';
import { useSeekPayTypes } from '../../hooks/useSeekPayTypes';
import { useSeekCurrencies } from '../../hooks/useSeekCurrencies';
import PropTypes from 'prop-types';
import ErrorIcon from '@mui/icons-material/Error';
import TextAreaWithCounter from '../UtilComponents/TextAreaWithCounter';
import { parseSalary } from '../../../NewUI/Components/Multiposter/salaryHelper';

function PayDetails({ answers, setAnswers, requiredRefs, activeError }) {
  const { payTypes } = useSeekPayTypes();
  const { currencies } = useSeekCurrencies();
  const classes = useStyles();

  const setSalaryType = (value) => {
    const [basisCode, intervalCode] = value.split(':');
    setAnswers({ ...answers, salary_type: basisCode, salary_interval: intervalCode });
  };

  return (
    <Box className={classes.payDetailsContainer}>
      <h4>Pay details</h4>

      <FormControl fullWidth={true} className={`${classes.formControl}`}>
        <FormLabel focused={false} className={classes.label}>
          Pay Type
          <span className={classes.asterisk}> *</span>
        </FormLabel>
        <RadioGroup
          value={`${answers.salary_type}:${answers.salary_interval || 'Hour'}`}
          onChange={(e) => setSalaryType(e.target.value)}
        >
          {payTypes.map((pay) => (
            <FormControlLabel
              key={pay.label}
              label={pay.label}
              value={`${pay.basisCode}:${pay.intervalCode}`}
              control={<Radio />}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.salary}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel focused={false} className={classes.label}>
          Pay Range
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.salary && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              {!answers.salary_min || !answers.salary_max ? (
                <span>This field is required</span>
              ) : (
                <span>The pay range cannot exceed $100,000</span>
              )}
            </div>
          )}
        </FormLabel>
        <span className={classes.subLabel}>
          Enter a pay range to offer candidates (this will not show on your ad)
        </span>
        <Grid container classes={{ root: classes.controlledNumberInput }}>
          <Grid
            item
            container
            alignContent="center"
            alignItems="center"
            justifyContent="center"
            wrap="nowrap"
          >
            <React.Fragment>
              <Box m={1} ml={0}>
                <TextField
                  error={activeError === requiredRefs.salary}
                  className={classes.textField}
                  variant="outlined"
                  onBlur={() => {
                    if (answers.salary_min > answers.salary_max)
                      setAnswers({ ...answers, salary_max: answers.salary_min });
                  }}
                  value={Number(answers.salary_min).toLocaleString()}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">From</InputAdornment>
                  }}
                  onChange={(e) =>
                    setAnswers({ ...answers, salary_min: parseSalary(e.target.value) })
                  }
                />
              </Box>
              <Box m={1}>
                <TextField
                  error={activeError === requiredRefs.salary}
                  className={classes.textField}
                  variant="outlined"
                  value={Number(answers.salary_max).toLocaleString()}
                  onBlur={() => {
                    if (answers.salary_max < answers.salary_min)
                      setAnswers({ ...answers, salary_max: answers.salary_min });
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">To</InputAdornment>
                  }}
                  onChange={(e) =>
                    setAnswers({ ...answers, salary_max: parseSalary(e.target.value) })
                  }
                />
              </Box>
              <Box m={1}>
                <Select
                  classes={{ select: classes.dropdown }}
                  variant="outlined"
                  value={answers.currency}
                  onChange={(e) => setAnswers({ ...answers, currency: e.target.value })}
                  displayEmpty={false}
                  renderValue={(value) => value}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.code}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <span className={classes.salaryIntervalLabel}>
                per {answers.salary_interval || 'Hour'}
              </span>
            </React.Fragment>
          </Grid>
        </Grid>
      </FormControl>

      <TextAreaWithCounter
        title="Customise the Pay information on your Ad (Optional)"
        value={answers.salary_additional_text}
        setValue={(value) => setAnswers({ ...answers, salary_additional_text: value })}
        maxLength={50}
        helperText="E.g. $50,000 + annual bonus"
      />
    </Box>
  );
}

PayDetails.propTypes = {
  answers: PropTypes.object.isRequired,
  setAnswers: PropTypes.func.isRequired,
  requiredRefs: PropTypes.object.isRequired,
  activeError: PropTypes.ref
};

export default PayDetails;
